import { createPortal } from "react-dom";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
  type RefObject
} from "react";
import { Handle, Position, type Edge, type Node, type NodeProps, useStore } from "@xyflow/react";
import { useOperationNodeOutputMap } from "./OperationNodeOutputsContext.js";
import type { NodeOutputRecord } from "./operationInspector/nodeInspectorWidgets.js";
import {
  derivePersistInputTopology,
  isOutputKindCompatibleWithInputPort,
  NODE_LABELS,
  NODE_OUTLINE_COLORS,
  NODE_PORT_SIGNATURES,
  type OperationGraphNodeType,
  outputDataKindForPort,
  type PortDataKind,
  type PortDefinition
} from "../lib/operationPorts.js";

type VizType =
  | "unknown"
  | "line"
  | "bar"
  | "combo"
  | "area"
  | "scatter"
  | "pie"
  | "donut"
  | "gauge"
  | "heatmap"
  | "funnel"
  | "radar"
  | "treemap"
  | "sunburst"
  | "kpi_card"
  | "tantable";

const LEGACY_VIZ_TYPES = new Set(["table_basic", "table_totals", "table_pivot", "table"]);

const VIZ_TYPE_OPTIONS: Array<{ value: VizType; label: string }> = [
  { value: "unknown", label: "Unknown" },
  { value: "line", label: "Line" },
  { value: "bar", label: "Bar" },
  { value: "tantable", label: "TanTable" }
];

function normalizeVizType(value: unknown): VizType {
  if (typeof value !== "string") return "unknown";
  if (LEGACY_VIZ_TYPES.has(value)) return "tantable";
  return (VIZ_TYPE_OPTIONS.find((option) => option.value === value)?.value ?? "unknown") as VizType;
}

function inferVizTypeFromSpec(specViz: unknown): VizType {
  if (!specViz || typeof specViz !== "object" || Array.isArray(specViz)) return "unknown";
  const record = specViz as Record<string, unknown>;
  const fromField = normalizeVizType(record.vizType);
  if (fromField !== "unknown") return fromField;
  if (record.kind === "tantable" || record.kind === "table" || record.kind === "grid") return "tantable";
  return "unknown";
}

function vizTypeGlyph(vizType: VizType): string {
  switch (vizType) {
    case "line":
      return "∿";
    case "bar":
      return "▮";
    case "combo":
      return "≋";
    case "area":
      return "◒";
    case "scatter":
      return "⋯";
    case "pie":
      return "◔";
    case "donut":
      return "◎";
    case "gauge":
      return "◠";
    case "heatmap":
      return "▦";
    case "funnel":
      return "∇";
    case "radar":
      return "✶";
    case "treemap":
      return "⧉";
    case "sunburst":
      return "☼";
    case "kpi_card":
      return "⊞";
    case "tantable":
      return "▥";
    default:
      return "?";
  }
}

// ADR-0128 — the async run API drives per-node run states. `waiting` = queued
// but not yet started; `from_cache` = the run reused this node's cached output
// (no re-execution). Both come from the polled run's node list.
export type NodeRunStatus = "idle" | "waiting" | "running" | "success" | "error" | "from_cache";

export type OperationCanvasNodeData = {
  nodeType: OperationGraphNodeType;
  label: string;
  description?: string | null;
  config: Record<string, unknown>;
  runStatus?: NodeRunStatus;
  /**
   * ADR-0128 — optional last-run metadata for the node's badge tooltip. Absolute
   * timestamps only (house rule — never relative). Populated from the polled
   * run's node list; absent when the node has no run history this session.
   */
  runFinishedAt?: string | null;
  /** from_cache provenance: when the served cache was originally produced ("data as of"). */
  cacheProducedAt?: string | null;
  needsValidation?: boolean;
  /** ADR-0146: set when this node's output schema is frozen. Neutral state —
   *  a locked production shape is healthy, not a warning. */
  schemaLockTitle?: string | null;
  /**
   * ADR-0153 Phase 5 — the node's worst validator-finding treatment, stamped
   * by the editor's findings sweep (type-agnostic; no inspector involvement).
   * `pending` is a declared unfinished-by-design state and renders neutral.
   */
  findingsSeverity?: "error" | "warning" | "pending" | null;
  /** Business-language tooltip for the findings badge (counts, no codes). */
  findingsTitle?: string | null;
  /**
   * Loop membership, stamped by the editor from the ONE topology derivation.
   *
   * `loopPairAccent` is set on both bookends AND on every step inside the
   * body. Two loops in one operation is the case that makes this load-bearing:
   * the bookends are joined by a config reference rather than an edge, so
   * nothing on the canvas otherwise says which end belongs to which start.
   */
  loopPairAccent?: string | null;
  /** The pair's name, derived from the START step's label. Never an id. */
  loopPairName?: string | null;
  /** True for steps INSIDE a body (not the bookends) — drives body tinting. */
  loopBodyMember?: boolean;
  /** Live progress on the start step while the loop runs, e.g. "3 of 12". */
  loopProgress?: string | null;
  /** Outcome summary on the end step: "9 of 12 · 3 failed". */
  loopOutcome?: string | null;
  /**
   * ADR-0216 §A7.13 — the Atlas Reports that keep this step each run (their
   * names). Atlas owns the step: it changes with the report's contract, and the
   * editor's saves cannot remove or change it while a report reads it.
   */
  keptBy?: string[] | null;
};

export type OperationCanvasNodeType = Node<OperationCanvasNodeData, "operationNode">;

function portKindCssVar(dataKind: PortDataKind): string {
  if (dataKind === "tabular") return "var(--op-port-tabular)";
  if (dataKind === "content") return "var(--op-port-content)";
  return "var(--op-port-references)";
}

function useDraggingSourceKind(): PortDataKind | null {
  return useStore((state) => {
    const c = state.connection as {
      inProgress?: boolean;
      from?: { handle?: { id?: string | null; type?: string | null } | null } | null;
    };
    if (!c?.inProgress || !c.from?.handle || c.from.handle.type !== "source") return null;
    const hid = (c.from.handle.id ?? "").split("@")[0];
    return outputDataKindForPort(hid);
  });
}

const PORT_TOOLTIP_SHOW_MS = 300;
const PORT_TOOLTIP_HIDE_MS = 400;

function usePortHoverTooltip() {
  const [visible, setVisible] = useState(false);
  const showRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hideRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (showRef.current) clearTimeout(showRef.current);
      if (hideRef.current) clearTimeout(hideRef.current);
    },
    []
  );

  const onEnter = useCallback(() => {
    if (hideRef.current) {
      clearTimeout(hideRef.current);
      hideRef.current = null;
    }
    if (showRef.current) clearTimeout(showRef.current);
    showRef.current = setTimeout(() => setVisible(true), PORT_TOOLTIP_SHOW_MS);
  }, []);

  const onLeave = useCallback(() => {
    if (showRef.current) {
      clearTimeout(showRef.current);
      showRef.current = null;
    }
    if (hideRef.current) clearTimeout(hideRef.current);
    hideRef.current = setTimeout(() => setVisible(false), PORT_TOOLTIP_HIDE_MS);
  }, []);

  return { visible, onEnter, onLeave };
}

/** Renders above other graph nodes / palette: fixed layer + body portal. */
function PortTooltipPortal({
  visible,
  anchorRef,
  wide,
  children
}: {
  visible: boolean;
  anchorRef: RefObject<HTMLDivElement | null>;
  wide: boolean;
  children: ReactNode;
}) {
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);

  useLayoutEffect(() => {
    if (!visible) {
      setPos(null);
      return;
    }
    const el = anchorRef.current;
    if (!el) return;

    const update = () => {
      const a = anchorRef.current;
      if (!a) return;
      const r = a.getBoundingClientRect();
      setPos({ left: r.left + r.width / 2, top: r.top });
    };

    update();
    const raf = requestAnimationFrame(update);
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [visible, anchorRef]);

  if (!visible || pos == null) return null;

  return createPortal(
    <div
      className={`opGraphPortTooltip opGraphPortTooltipPortal${wide ? " opGraphPortTooltipWide" : ""}`}
      style={{ left: pos.left, top: pos.top, transform: "translate(-50%, calc(-100% - 8px))" }}
      role="tooltip"
    >
      {children}
    </div>,
    document.body
  );
}

function stripPortHandle(h: string | null | undefined): string {
  if (h == null || h === "") return "";
  const at = h.indexOf("@");
  return at > 0 ? h.slice(0, at) : h;
}

function formatTabularRuntimeLine(outputColCount: number | null | undefined, outputRowCount: number | null | undefined): string {
  if (outputColCount != null && outputRowCount != null) {
    return `${outputColCount} column${outputColCount !== 1 ? "s" : ""}, ${outputRowCount.toLocaleString()} row${outputRowCount !== 1 ? "s" : ""}`;
  }
  if (outputRowCount != null) {
    return `${outputRowCount.toLocaleString()} row${outputRowCount !== 1 ? "s" : ""}`;
  }
  if (outputColCount != null) {
    return `${outputColCount} column${outputColCount !== 1 ? "s" : ""}`;
  }
  return "No data yet";
}

function ariaPortSummary(p: PortDefinition): string {
  return `${p.label}, ${p.id === "scope_in" ? "tabular" : p.dataKind}`;
}

type InputPortUpstreamTooltip =
  | { kind: "none" }
  | { kind: "connected"; lines: Array<{ key: string; text: string }> };

/** Tooltip stats from `nodeOutputs` (hub), not denormalized `node.data`. */
function buildInputPortUpstreamFromOutputs(
  edges: Edge[],
  canvasNodes: Node<OperationCanvasNodeData>[],
  nodeOutputMap: Record<string, NodeOutputRecord>,
  nodeId: string,
  targetPortId: string
): InputPortUpstreamTooltip {
  const incoming = edges.filter((e) => {
    if (e.target !== nodeId) return false;
    return stripPortHandle(e.targetHandle) === targetPortId;
  });
  const sorted = [...incoming].sort((a, b) => a.id.localeCompare(b.id));
  if (sorted.length === 0) return { kind: "none" };
  const lines = sorted.map((e) => {
    const src = canvasNodes.find((n) => n.id === e.source);
    const d = src?.data;
    const label = (d?.label?.trim() || (d ? NODE_LABELS[d.nodeType] : null) || "Upstream").slice(0, 80);
    const rec = nodeOutputMap[e.source];
    const stats = formatTabularRuntimeLine(rec?.outputSchema?.length ?? null, rec?.rowCount ?? null);
    return { key: e.id, text: `${label}: ${stats}` };
  });
  return { kind: "connected", lines };
}

function OutputPortTooltipContent({
  port: p,
  outputColCount,
  outputRowCount
}: {
  port: PortDefinition;
  outputColCount?: number | null;
  outputRowCount?: number | null;
}) {
  const tabularLike = p.dataKind === "tabular" || p.id === "scope_in";

  let runtimeLine = "No data yet";
  if (tabularLike) {
    runtimeLine = formatTabularRuntimeLine(outputColCount, outputRowCount);
  }

  return (
    <div className="opGraphPortTooltipInner">
      <div className="opGraphPortTooltipTitle">{p.label}</div>
      <div className="opGraphPortTooltipRuntime">{runtimeLine}</div>
    </div>
  );
}

function InputPortTooltipContent({ port: p, upstream }: { port: PortDefinition; upstream: InputPortUpstreamTooltip }) {
  const tabularLike = p.dataKind === "tabular" || p.id === "scope_in";

  let runtime: ReactNode;
  if (!tabularLike) {
    runtime = <div className="opGraphPortTooltipRuntime">—</div>;
  } else if (upstream.kind === "none") {
    runtime = <div className="opGraphPortTooltipRuntime">Not connected</div>;
  } else {
    runtime = (
      <div className="opGraphPortTooltipUpstreamList">
        {upstream.lines.map((line) => (
          <div key={line.key} className="opGraphPortTooltipUpstreamLine">
            {line.text}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="opGraphPortTooltipInner">
      <div className="opGraphPortTooltipTitle">{p.label}</div>
      {runtime}
    </div>
  );
}

function CanvasTargetPortRow({
  port: p,
  highlight,
  dim,
  nodeId
}: {
  port: PortDefinition;
  highlight: boolean;
  dim: boolean;
  nodeId: string;
}) {
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const { visible, onEnter, onLeave } = usePortHoverTooltip();
  const nodeOutputMap = useOperationNodeOutputMap();
  const edges = useStore((s) => s.edges as Edge[]);
  const canvasNodes = useStore((s) => s.nodes as Node<OperationCanvasNodeData>[]);
  const upstream = useMemo(
    () => buildInputPortUpstreamFromOutputs(edges, canvasNodes, nodeOutputMap, nodeId, p.id),
    [edges, canvasNodes, nodeOutputMap, nodeId, p.id]
  );
  const wide = upstream.kind === "connected" && upstream.lines.length > 1;

  return (
    <div ref={anchorRef} className="opGraphPortSlot">
      <div className="opGraphPortHit">
        <div className="opGraphPortGlyph">
          <PortShape portId={p.id} dataKind={p.dataKind} highlight={highlight} dim={dim} />
        </div>
        <Handle
          type="target"
          position={Position.Left}
          id={p.id}
          className="opGraphPortHandle"
          aria-label={ariaPortSummary(p)}
          onMouseEnter={onEnter}
          onMouseLeave={onLeave}
        />
      </div>
      <PortTooltipPortal visible={visible} anchorRef={anchorRef} wide={wide}>
        <InputPortTooltipContent port={p} upstream={upstream} />
      </PortTooltipPortal>
    </div>
  );
}

function CanvasSourcePortRow({ port: p, canvasNodeId }: { port: PortDefinition; canvasNodeId: string }) {
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const { visible, onEnter, onLeave } = usePortHoverTooltip();
  const nodeOutputMap = useOperationNodeOutputMap();
  const rec = nodeOutputMap[canvasNodeId];
  const outputColCount = rec?.outputSchema?.length ?? null;
  const outputRowCount = rec?.rowCount ?? null;

  return (
    <div ref={anchorRef} className="opGraphPortSlot">
      <div className="opGraphPortHit">
        <div className="opGraphPortGlyph">
          <PortShape portId={p.id} dataKind={p.dataKind} highlight={false} dim={false} />
        </div>
        <Handle
          type="source"
          position={Position.Right}
          id={p.id}
          className="opGraphPortHandle"
          aria-label={ariaPortSummary(p)}
          onMouseEnter={onEnter}
          onMouseLeave={onLeave}
        />
      </div>
      <PortTooltipPortal visible={visible} anchorRef={anchorRef} wide={false}>
        <OutputPortTooltipContent port={p} outputColCount={outputColCount} outputRowCount={outputRowCount} />
      </PortTooltipPortal>
    </div>
  );
}

function PortShape({
  portId,
  dataKind,
  highlight,
  dim
}: {
  portId: string;
  dataKind: PortDataKind;
  highlight: boolean;
  dim: boolean;
}) {
  const color = portId === "scope_in" ? "var(--op-port-scope)" : portKindCssVar(dataKind);
  const base: CSSProperties = {
    width: 10,
    height: 10,
    flexShrink: 0,
    opacity: dim ? 0.25 : 1,
    boxShadow: highlight ? `0 0 0 3px ${color}55` : undefined,
    transition: "opacity 120ms ease, box-shadow 120ms ease"
  };

  if (portId === "scope_in") {
    return (
      <div
        style={{
          ...base,
          width: 0,
          height: 0,
          borderLeft: "5px solid transparent",
          borderRight: "5px solid transparent",
          borderTop: `8px solid ${color}`,
          background: "transparent",
          boxShadow: highlight ? `0 0 0 2px var(--op-port-scope)44` : undefined
        }}
      />
    );
  }

  if (dataKind === "content") {
    return (
      <div
        style={{
          ...base,
          background: color,
          transform: "rotate(45deg)",
          borderRadius: 1,
          border: "2px solid var(--panel, #fff)"
        }}
      />
    );
  }

  if (dataKind === "references") {
    return (
      <div
        style={{
          ...base,
          background: "transparent",
          transform: "rotate(45deg)",
          borderRadius: 1,
          border: `2px solid ${color}`
        }}
      />
    );
  }

  return (
    <div
      style={{
        ...base,
        borderRadius: "50%",
        background: color,
        border: "2px solid var(--panel, #fff)"
      }}
    />
  );
}

export function OperationCanvasNode({ id, data, selected }: NodeProps<OperationCanvasNodeType>) {
  const t = data.nodeType;
  const outlineColor = NODE_OUTLINE_COLORS[t];
  const rs = data.runStatus ?? "idle";
  const isRunning = rs === "running";
  const isWaiting = rs === "waiting";
  const isFromCache = rs === "from_cache";
  const dragOutKind = useDraggingSourceKind();

  // ADR-0128 — badge tooltip. Absolute timestamps only (house rule).
  const badgeTooltip = (() => {
    const finishedLabel = data.runFinishedAt ? new Date(data.runFinishedAt).toLocaleString() : null;
    // Provenance, not this run's finish time — "data as of" is when the served
    // cache was originally produced.
    const producedLabel = data.cacheProducedAt ? new Date(data.cacheProducedAt).toLocaleString() : null;
    switch (rs) {
      case "waiting":
        return "Queued";
      case "running":
        return "Running";
      case "from_cache":
        return producedLabel ? `Served from cache — data as of ${producedLabel}` : "Served from cache";
      case "success":
        return finishedLabel ? `Succeeded — ${finishedLabel}` : "Succeeded";
      case "error":
        return finishedLabel ? `Failed — ${finishedLabel}` : "Failed";
      default:
        return rs;
    }
  })();

  const border = selected ? `3px solid ${outlineColor}` : `2px solid ${outlineColor}`;
  const selectedBoxShadow = selected
    ? outlineColor.startsWith("var(")
      ? `0 0 0 2px color-mix(in srgb, ${outlineColor} 22%, transparent)`
      : `0 0 0 2px ${outlineColor}33`
    : undefined;
  const ports = NODE_PORT_SIGNATURES[t] ?? [];
  const inputs = ports.filter((p) => p.direction === "in");
  const outputs = ports.filter((p) => p.direction === "out");

  // ADR-0102 — persist's input topology is policy-driven. Snapshot policy
  // shows only `tabular_in_incremental`; keyed policies show both
  // `tabular_in_initial` + `tabular_in_incremental`. The active port set is
  // derived from `spec_persist.config.write_mode` (defaulting to snapshot).
  const persistTopology = useMemo(() => {
    if (t !== "persist") return null;
    const cfg = (data.config as { spec_persist?: { config?: { write_mode?: string } } } | undefined)?.spec_persist?.config;
    return derivePersistInputTopology(typeof cfg?.write_mode === "string" ? cfg.write_mode : undefined);
  }, [t, data.config]);

  const scopePorts = inputs.filter((p) => p.id === "scope_in");
  const dataInputs = inputs
    .filter((p) => p.id !== "scope_in")
    .filter((p) => {
      // Persist: hide ports not in the active policy's topology.
      if (t === "persist" && persistTopology) {
        const active = new Set<string>(persistTopology.ports);
        return active.has(p.id);
      }
      return true;
    });

  function targetHighlight(portId: string): boolean {
    if (!dragOutKind || !isOutputKindCompatibleWithInputPort(dragOutKind, portId)) return false;
    return true;
  }

  function targetDim(portId: string): boolean {
    if (!dragOutKind) return false;
    return !isOutputKindCompatibleWithInputPort(dragOutKind, portId);
  }

  return (
    <div
      className={`opGraphNode${data.loopBodyMember ? " opLoopBody" : ""}`}
      style={data.loopPairAccent ? ({ "--op-loop-body-accent": data.loopPairAccent } as CSSProperties) : undefined}
    >
      {inputs.length > 0 ? (
        <div className="opGraphNodePortsCol opGraphNodePortsColIn">
          {scopePorts.map((p) => (
            <CanvasTargetPortRow key={p.id} port={p} highlight={targetHighlight(p.id)} dim={targetDim(p.id)} nodeId={id} />
          ))}
          {scopePorts.length > 0 && dataInputs.length > 0 ? <div style={{ height: 4, flexShrink: 0 }} /> : null}
          {dataInputs.map((p) => (
            <CanvasTargetPortRow key={p.id} port={p} highlight={targetHighlight(p.id)} dim={targetDim(p.id)} nodeId={id} />
          ))}
        </div>
      ) : null}

      <div
        style={{
          position: "relative",
          flex: 1,
          minWidth: 180,
          padding: 10,
          background: "var(--surface, var(--panel))",
          border,
          borderRadius: 8,
          boxShadow: selectedBoxShadow
        }}
      >
        {isRunning ? <div className="opNodeRunningRing" style={{ color: outlineColor }} /> : null}
        {/* Queued: static dimmed ring, no pulse. */}
        {isWaiting ? <div className="opNodeWaitingRing" /> : null}
        {/* Served-from-cache: a subtle cache pill instead of the running pulse. */}
        {isFromCache ? (
          <span className="opNodeCachePill" title={badgeTooltip} aria-label={badgeTooltip}>⚡</span>
        ) : null}

        {rs !== "idle" && !data.needsValidation ? (
          <div className={`opNodeStatusBadge ${rs}`} title={badgeTooltip} />
        ) : null}

        {t === "ai_provider" ? (
          <span
            className={`opNodeAiModePill pill ${
              String((data.config as { mode?: string })?.mode ?? "authoring") === "production" ? "pillOk" : "pillWarn"
            }`}
            title={
              String((data.config as { mode?: string })?.mode ?? "authoring") === "production"
                ? "Production mode"
                : "Authoring mode"
            }
          >
            {String((data.config as { mode?: string })?.mode ?? "authoring") === "production" ? "PROD" : "AUTH"}
          </span>
        ) : null}

        {data.findingsSeverity ? (
          <div
            className={`opNodeFindingsBadge ${data.findingsSeverity}`}
            title={data.findingsTitle ?? undefined}
            aria-label={data.findingsTitle ?? "This step has validation findings"}
          >
            {data.findingsSeverity === "pending" ? "…" : "!"}
          </div>
        ) : null}

        {data.needsValidation ? (
          <div
            className="opNodeStatusBadge"
            title="Needs validation — upstream schema changed"
            style={{
              position: "absolute",
              top: -6,
              right: -6,
              width: 16,
              height: 16,
              borderRadius: "50%",
              background: "#f59e0b",
              border: "2px solid var(--surface, var(--panel))",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 10,
              fontWeight: 800,
              color: "#fff",
              lineHeight: 1
            }}
          >
            !
          </div>
        ) : null}

        <div
          style={{ fontWeight: 700, marginBottom: 4, display: "flex", alignItems: "center", gap: 6 }}
          title={t === "file_provider" ? "File Provider — produces tabular data from an uploaded file" : undefined}
        >
          {t === "file_provider" ? (
            <span aria-label="File Provider" style={{ fontSize: 14, lineHeight: 1 }}>⬆</span>
          ) : null}
          <span>{data.label || NODE_LABELS[t]}</span>
          {/* ADR-0146 — schema lock. Muted, alongside the label: this is a
              healthy production state, not a status to alarm about. */}
          {data.schemaLockTitle ? (
            <span
              className="muted"
              aria-label="Schema locked"
              title={data.schemaLockTitle}
              style={{ fontSize: 11, lineHeight: 1 }}
            >
              🔒
            </span>
          ) : null}
        </div>
        <div className="muted" style={{ fontSize: 12 }}>
          {NODE_LABELS[t]}
          {t === "persist" && persistTopology ? ` · ${persistTopology.label}` : ""}
        </div>
        {data.keptBy && data.keptBy.length > 0 ? (
          <div
            className="muted"
            style={{ fontSize: 11, marginTop: 2 }}
            title="Atlas keeps this step for the report and changes it when the report's contract changes. It cannot be removed or edited here while the report reads it."
          >
            Kept each run by {data.keptBy.join(", ")}
          </div>
        ) : null}

        {/* The pair name, on BOTH bookends, in the pair's own accent. This is
            what says which end belongs to which start when an operation holds
            more than one loop. Derived from the start step's label — never a
            node id, which is the one thing the relationship is stored as and
            the one thing the user must not be shown. */}
        {(t === "loop_start" || t === "loop_end") && data.loopPairName ? (
          <div
            className="opLoopPairName"
            style={data.loopPairAccent ? ({ color: data.loopPairAccent } as CSSProperties) : undefined}
            title={`Both ends of this loop carry this name: ${data.loopPairName}`}
          >
            {data.loopPairName}
          </div>
        ) : null}

        {/* Iteration progress while running, then the outcome. A partly-failed
            loop is neither a clean success nor a plain failure, so the end step
            says so rather than wearing a bare green badge. */}
        {t === "loop_start" && data.loopProgress ? (
          <div className="opLoopProgress">{data.loopProgress}</div>
        ) : null}
        {t === "loop_end" && data.loopOutcome ? (
          <div className="opLoopProgress">{data.loopOutcome}</div>
        ) : null}
      </div>

      {outputs.length > 0 ? (
        <div className="opGraphNodePortsCol opGraphNodePortsColOut">
          {outputs.map((p) => (
            <CanvasSourcePortRow key={p.id} port={p} canvasNodeId={id} />
          ))}
        </div>
      ) : null}
    </div>
  );
}
